import React, { useCallback, useMemo, useState } from 'react';

import { Alert, Box } from '@mui/material';

import { useEditExtranetChange, useGetExtranetPendingChange } from '~/hooks/apis/extranet/useExtranetPendingChanges';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import BulkActionBar from './BulkActionBar';
import ChangeDiffDialog from './ChangeDiffDialog';
import ConfirmActionDialog from './ConfirmActionDialog';
import ExtranetApprovalsPageHeader from './ExtranetApprovalsPageHeader';
import ExtranetApprovalsTable from './ExtranetApprovalsTable';
import ExtranetApprovalsTabs from './ExtranetApprovalsTabs';
import FilterToolbar from './FilterToolbar';
import ReplayFailureAlert from './ReplayFailureAlert';
import useExtranetApprovalActions from './hooks/useExtranetApprovalActions';
import useExtranetApprovalsData from './hooks/useExtranetApprovalsData';
import useExtranetApprovalsSelection from './hooks/useExtranetApprovalsSelection';
import useExtranetApprovalsUrlState from './hooks/useExtranetApprovalsUrlState';
import useExtranetChangeMetadata from './hooks/useExtranetChangeMetadata';

export default function ExtranetApprovalsPage() {
  const urlState = useExtranetApprovalsUrlState();
  const { counts, error, isLoading, rows, total } = useExtranetApprovalsData(urlState.appliedFilters);
  const editChange = useEditExtranetChange();
  const selectableRows = useMemo(() => (urlState.isPendingTab ? rows : []), [rows, urlState.isPendingTab]);
  const selection = useExtranetApprovalsSelection(selectableRows);
  const actions = useExtranetApprovalActions({ onSuccess: selection.clearSelection });

  const [diffRow, setDiffRow] = useState<ExtranetPendingChange | null>(null);
  const activeDiffChangeId = diffRow?.id ?? urlState.deepLinkedDiffRow?.id ?? '';
  const { data: activeDiffChangeData } = useGetExtranetPendingChange(activeDiffChangeId);

  const handleSaveChange = async (
    id: string,
    proposedValue: Record<string, unknown>,
    expectedProposedValue: Record<string, unknown>,
  ) => {
    const response = await editChange.mutateAsync({ id, proposedValue, expectedProposedValue });
    setDiffRow(response.result);
    return response.result;
  };

  const activeDiffRow = activeDiffChangeData?.result ?? diffRow ?? urlState.deepLinkedDiffRow;

  const visibleChanges = useMemo(() => {
    const changes = [...rows];
    if (urlState.deepLinkedChange && !changes.some((row) => row.id === urlState.deepLinkedChange?.id)) {
      changes.push(urlState.deepLinkedChange);
    }
    if (activeDiffRow && !changes.some((row) => row.id === activeDiffRow.id)) {
      changes.push(activeDiffRow);
    }
    return changes;
  }, [activeDiffRow, rows, urlState.deepLinkedChange]);

  const metadataByChangeId = useExtranetChangeMetadata(visibleChanges);

  const handleSearch = useCallback(() => {
    selection.clearSelection();
    urlState.handleSearch();
  }, [selection, urlState]);

  const handleClear = useCallback(() => {
    selection.clearSelection();
    urlState.handleClear();
  }, [selection, urlState]);

  const handleTabChange = useCallback(
    (tab: number) => {
      selection.clearSelection();
      urlState.changeTab(tab);
    },
    [selection, urlState],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      selection.clearSelection();
      urlState.changePage(nextPage);
    },
    [selection, urlState],
  );

  return (
    <Box>
      <ExtranetApprovalsPageHeader pendingCount={counts.pending} homesVillasPendingCount={counts.homesVillasPending} />

      <ExtranetApprovalsTabs activeTab={urlState.activeTab} onChange={handleTabChange} />

      <FilterToolbar
        filters={urlState.filters}
        setFilters={urlState.setFilters}
        showRequestTypeFilter={urlState.isHistoryTab}
        showStatusFilter={urlState.isHistoryTab}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      <BulkActionBar
        selectedCount={selection.selectedIds.length}
        isMutating={actions.isMutating}
        onBulkApprove={() => {
          const ids = selection.selectedIds;
          const row = ids.length === 1 ? rows.find((candidate) => candidate.id === ids[0]) : undefined;
          actions.setConfirmAction({
            type: 'approve',
            ids,
            ...(row ? { expectedProposedValues: { [row.id]: row.proposed_value } } : {}),
          });
        }}
        onBulkReject={() => actions.setConfirmAction({ type: 'reject', ids: selection.selectedIds })}
      />

      {error instanceof Error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      {actions.replayFailures.length > 0 && (
        <ReplayFailureAlert failures={actions.replayFailures} onClose={() => actions.setReplayFailures([])} />
      )}

      <ExtranetApprovalsTable
        rows={rows}
        total={total}
        page={urlState.page}
        isLoading={isLoading}
        isPendingTab={urlState.isPendingTab}
        isHistoryTab={urlState.isHistoryTab}
        isMutating={actions.isMutating}
        metadataByChangeId={metadataByChangeId}
        selected={selection.selected}
        allSelected={selection.allSelected}
        onToggleSelect={selection.toggleSelect}
        onToggleSelectAll={selection.toggleSelectAll}
        onViewDiff={setDiffRow}
        onRowAction={(type, id) => {
          if (type === 'approve') {
            const row = rows.find((candidate) => candidate.id === id);
            if (!row) {
              return;
            }
            actions.setConfirmAction({
              type,
              ids: [id],
              expectedProposedValues: { [id]: row.proposed_value },
            });
          } else {
            actions.setConfirmAction({ type, ids: [id] });
          }
        }}
        onPageChange={handlePageChange}
      />

      <ChangeDiffDialog
        change={activeDiffRow}
        metadata={activeDiffRow ? metadataByChangeId.get(activeDiffRow.id) : undefined}
        isSaving={editChange.isPending}
        onClose={() => {
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
        onEdit={handleSaveChange}
        onApprove={(id, expectedProposedValue) => {
          actions.setConfirmAction({
            type: 'approve',
            ids: [id],
            expectedProposedValues: { [id]: expectedProposedValue },
          });
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
        onReject={(id) => {
          actions.setConfirmAction({ type: 'reject', ids: [id] });
          setDiffRow(null);
          urlState.clearDeepLinkedDiffRow();
        }}
      />

      <ConfirmActionDialog
        action={actions.confirmAction}
        isMutating={actions.isMutating}
        onClose={() => actions.setConfirmAction(null)}
        onConfirm={actions.handleConfirmAction}
      />
    </Box>
  );
}
